import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  AttachFile,
  Person,
  School,
  PriorityHigh,
  Schedule,
  Close,
  Add,
} from '@mui/icons-material';

interface Teacher {
  id: number;
  name: string;
  subject: string;
  email: string;
  avatar?: string;
  children: string[]; // Children they teach
}

interface Child {
  id: number;
  name: string;
  grade: string;
  teachers: number[]; // Teacher IDs
}

interface MessageDraft {
  recipientId: number | null;
  recipientName: string;
  childId: number | null;
  childName: string;
  subject: string;
  content: string;
  priority: 'normal' | 'high' | 'urgent';
  requestMeeting: boolean;
  attachments: File[];
}

const ParentComposeMessage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [draft, setDraft] = useState<MessageDraft>({
    recipientId: null,
    recipientName: '',
    childId: null,
    childName: '',
    subject: '',
    content: '',
    priority: 'normal',
    requestMeeting: false,
    attachments: []
  });

  // Pre-populate if coming from a specific context
  const preSelectedTeacherId = searchParams.get('teacherId');
  const preSelectedChildId = searchParams.get('childId');
  const preSelectedSubject = searchParams.get('subject');

  useEffect(() => {
    // Mock data - replace with API calls
    setLoading(true);
    setTimeout(() => {
      const mockChildren: Child[] = [
        { id: 1, name: 'Emma Johnson', grade: '6th Grade', teachers: [1, 2, 3] },
        { id: 2, name: 'Lucas Johnson', grade: '3rd Grade', teachers: [4, 5, 6] },
      ];

      const mockTeachers: Teacher[] = [
        { 
          id: 1, 
          name: 'Ms. Sarah Wilson', 
          subject: 'Mathematics', 
          email: 'sarah.wilson@school.edu',
          children: ['Emma Johnson']
        },
        { 
          id: 2, 
          name: 'Mr. David Brown', 
          subject: 'English Literature', 
          email: 'david.brown@school.edu',
          children: ['Emma Johnson']
        },
        { 
          id: 3, 
          name: 'Dr. Emily Chen', 
          subject: 'Science', 
          email: 'emily.chen@school.edu',
          children: ['Emma Johnson']
        },
        { 
          id: 4, 
          name: 'Mrs. Lisa Garcia', 
          subject: 'History', 
          email: 'lisa.garcia@school.edu',
          children: ['Lucas Johnson']
        },
        { 
          id: 5, 
          name: 'Mr. James Miller', 
          subject: 'Art', 
          email: 'james.miller@school.edu',
          children: ['Lucas Johnson']
        },
        { 
          id: 6, 
          name: 'Ms. Anna Rodriguez', 
          subject: 'Physical Education', 
          email: 'anna.rodriguez@school.edu',
          children: ['Lucas Johnson']
        },
      ];

      setChildren(mockChildren);
      setTeachers(mockTeachers);

      // Pre-populate based on URL parameters
      if (preSelectedTeacherId) {
        const teacher = mockTeachers.find(t => t.id === parseInt(preSelectedTeacherId));
        if (teacher) {
          setDraft(prev => ({
            ...prev,
            recipientId: teacher.id,
            recipientName: teacher.name
          }));
        }
      }

      if (preSelectedChildId) {
        const child = mockChildren.find(c => c.id === parseInt(preSelectedChildId));
        if (child) {
          setDraft(prev => ({
            ...prev,
            childId: child.id,
            childName: child.name
          }));
        }
      }

      if (preSelectedSubject) {
        setDraft(prev => ({
          ...prev,
          subject: decodeURIComponent(preSelectedSubject)
        }));
      }

      setLoading(false);
    }, 1000);
  }, [preSelectedTeacherId, preSelectedChildId, preSelectedSubject]);

  const handleSendMessage = async () => {
    if (!draft.recipientId || !draft.subject.trim() || !draft.content.trim()) {
      return;
    }

    setSending(true);
    
    // Mock sending - replace with API call
    setTimeout(() => {
      console.log('Sending message:', draft);
      
      // Navigate to the new thread (mock thread ID)
      const newThreadId = Math.floor(Math.random() * 1000);
      navigate(`/parent/messages/thread/${newThreadId}`);
      setSending(false);
    }, 2000);
  };

  const getAvailableTeachers = () => {
    if (!draft.childId) return teachers;
    
    const child = children.find(c => c.id === draft.childId);
    if (!child) return [];
    
    return teachers.filter(t => child.teachers.includes(t.id));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getSubjectSuggestions = () => {
    const commonSubjects = [
      'Academic Progress Discussion',
      'Homework Concerns',
      'Behavioral Issues',
      'Parent-Teacher Meeting Request',
      'Assignment Clarification',
      'Attendance Question',
      'Extracurricular Activities',
      'Special Needs Support',
      'Academic Achievement Recognition',
      'Course Selection Guidance'
    ];

    if (draft.recipientId) {
      const teacher = teachers.find(t => t.id === draft.recipientId);
      if (teacher) {
        return [
          `${teacher.subject} - Progress Update`,
          `${teacher.subject} - Assignment Help`,
          `${teacher.subject} - Test Preparation`,
          ...commonSubjects
        ];
      }
    }

    return commonSubjects;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-messages')}
            sx={{ cursor: 'pointer' }}
          >
            Messages
          </Link>
          <Typography color="text.primary">New Message</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/parent-messages')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              New Message
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Compose a message to your child's teacher
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/parent-messages')}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={sending ? <CircularProgress size={20} /> : <Send />}
            onClick={handleSendMessage}
            disabled={!draft.recipientId || !draft.subject.trim() || !draft.content.trim() || sending}
          >
            {sending ? 'Sending...' : 'Send Message'}
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Child Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Child</InputLabel>
            <Select
              value={draft.childId || ''}
              label="Select Child"
              onChange={(e) => {
                const childId = e.target.value as number;
                const child = children.find(c => c.id === childId);
                setDraft(prev => ({
                  ...prev,
                  childId: childId,
                  childName: child?.name || '',
                  recipientId: null, // Reset teacher selection
                  recipientName: ''
                }));
              }}
            >
              {children.map((child) => (
                <MenuItem key={child.id} value={child.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      {child.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1">{child.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {child.grade}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Teacher Selection */}
          <FormControl fullWidth sx={{ mb: 3 }} disabled={!draft.childId}>
            <InputLabel>Select Teacher</InputLabel>
            <Select
              value={draft.recipientId || ''}
              label="Select Teacher"
              onChange={(e) => {
                const teacherId = e.target.value as number;
                const teacher = teachers.find(t => t.id === teacherId);
                setDraft(prev => ({
                  ...prev,
                  recipientId: teacherId,
                  recipientName: teacher?.name || ''
                }));
              }}
            >
              {getAvailableTeachers().map((teacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                      {teacher.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1">{teacher.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {teacher.subject} • {teacher.email}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Subject */}
          <Autocomplete
            freeSolo
            options={getSubjectSuggestions()}
            value={draft.subject}
            onChange={(event, newValue) => {
              setDraft(prev => ({
                ...prev,
                subject: newValue || ''
              }));
            }}
            onInputChange={(event, newInputValue) => {
              setDraft(prev => ({
                ...prev,
                subject: newInputValue
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Subject"
                placeholder="What would you like to discuss?"
                fullWidth
                required
                sx={{ mb: 3 }}
              />
            )}
          />

          {/* Priority Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={draft.priority}
              label="Priority"
              onChange={(e) => setDraft(prev => ({ ...prev, priority: e.target.value as 'normal' | 'high' | 'urgent' }))}
            >
              <MenuItem value="normal">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Normal" size="small" />
                  <Typography>Standard message</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="high">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="High" color="warning" size="small" />
                  <Typography>Requires prompt attention</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="urgent">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Urgent" color="error" size="small" />
                  <Typography>Immediate response needed</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Message Content */}
          <TextField
            label="Message"
            placeholder="Type your message here..."
            multiline
            rows={8}
            fullWidth
            required
            value={draft.content}
            onChange={(e) => setDraft(prev => ({ ...prev, content: e.target.value }))}
            sx={{ mb: 3 }}
          />

          {/* Additional Options */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.requestMeeting}
                  onChange={(e) => setDraft(prev => ({ ...prev, requestMeeting: e.target.checked }))}
                />
              }
              label="Request a meeting to discuss this topic"
            />
          </Box>

          {/* Attachments */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<AttachFile />}
              component="label"
            >
              Attach Files
              <input
                type="file"
                hidden
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setDraft(prev => ({ ...prev, attachments: [...prev.attachments, ...files] }));
                }}
              />
            </Button>
            
            {draft.attachments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Attached Files:
                </Typography>
                {draft.attachments.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.name}
                    onDelete={() => {
                      setDraft(prev => ({
                        ...prev,
                        attachments: prev.attachments.filter((_, i) => i !== index)
                      }));
                    }}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Message Preview */}
          {draft.recipientName && draft.subject && (
            <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Message Preview:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  {draft.recipientName.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2">
                    <strong>To:</strong> {draft.recipientName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Re:</strong> {draft.subject}
                  </Typography>
                  <Typography variant="body2">
                    <strong>About:</strong> {draft.childName}
                  </Typography>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                  <Chip 
                    label={draft.priority} 
                    color={getPriorityColor(draft.priority) as any}
                    size="small" 
                  />
                </Box>
              </Box>
              {draft.requestMeeting && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    📅 Meeting request will be included with this message
                  </Typography>
                </Alert>
              )}
            </Paper>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ParentComposeMessage;

